import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createTireModel,
  deleteTireModel,
  getTireModelById,
  getTireModelsList,
  updateTireModel,
  updateTireModelStatus,
  type TireModelListParams,
  type TireModelPayload,
  type TireModelStatusPayload,
} from "../../services/administrator/tireModelService";

export const useQueryGetTireModelsList = (
  params: TireModelListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.tireModelList, params, language],
      queryFn: () => getTireModelsList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useQueryGetTireModelById = (
  id?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.tireModelDetail, id, language],
    queryFn: () => getTireModelById(id!, language),
    enabled: Boolean(id) && options.enabled !== false,
    refetchOnMount: "always",
    retry: false,
    staleTime: 0,
    ...options,
  });
};

export const useMutationCreateTireModel = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: TireModelPayload) => createTireModel(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tireModelList,
      }),
  });
};

export const useMutationUpdateTireModel = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: TireModelPayload & { id: string | number };
    }) => updateTireModel(id, payload, language),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.tireModelList,
        }),
        queryClient.invalidateQueries({
          queryKey: QueryNames.tireModelDetail,
          refetchType: "none",
        }),
      ]),
  });
};

export const useMutationUpdateTireModelStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: TireModelStatusPayload) =>
      updateTireModelStatus(payload.id, payload, language),
    onSuccess: (_data, payload) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.tireModelList,
        }),
        queryClient.removeQueries({
          queryKey: [...QueryNames.tireModelDetail, payload.id],
        }),
      ]),
  });
};

export const useMutationDeleteTireModel = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteTireModel(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tireModelList,
      }),
  });
};
